import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  INVOICE_STATUSES,
  LOSS_REASONS,
  LOSS_REASON_LABELS,
  PAYMENT_METHODS,
  type Expense,
  type ExpenseAttachment,
  type ExpenseInput,
  type Platform,
  accountLabel,
  expenseCostType,
  type ExpenseAccount,
} from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import {
  apiErrorMessage,
  attachmentUrl,
  createExpense,
  fetchFxRate,
  removeAttachment,
  updateExpense,
  uploadAttachment,
} from "../services/expensesService";
import { fetchClients, type WorkHoursClientRow } from "../../work-hours/services/workHoursService";
import { categoryOptions, money } from "../lib/format";
import { attributionOf, type Attribution } from "../lib/reimbursable";
import { INVOICE_TONES, ui } from "../theme";
import { Chip, Dialog, ErrorBox, Field, FileDrop, Spinner } from "./ui";
import StatementViewer from "./StatementViewer";

// Alta / edición de un gasto (#608) + sus archivos: comprobante y factura (#610).

const today = () => new Date(Date.now() - 6 * 3600 * 1000).toISOString().slice(0, 10);

type FormState = {
  date: string;
  amount: string;
  currency: string;
  exchangeRate: string;
  vendor: string;
  category: string;
  description: string;
  justification: string;
  paymentMethod: string;
  cardLast4: string;
  platformId: string;
  /** Cliente al que se atribuye (#980); vacío = gasto propio. */
  clientId: string;
  /**
   * `company`: lo pagó la empresa. `reimbursable`: lo pagó una persona y la
   * empresa se lo debe. `not_reimbursable`: es de la persona (sale del resumen).
   */
  attribution: Attribution;
  /** `fijo`, `variable` o vacío = el de su categoría. */
  costType: string;
  /** Pérdida (#1177): el gasto no dejó nada (sin uso, cobro indebido, multa…). */
  loss: boolean;
  lossReason: string;
  lossNote: string;
  accountId: string;
  invoiceStatus: string;
  status: string;
  notes: string;
};

const toForm = (e?: Partial<Expense> | null): FormState => ({
  date: e?.date || today(),
  amount: e?.amount != null ? String(e.amount) : "",
  currency: e?.currency || "MXN",
  exchangeRate: e?.exchangeRate != null ? String(e.exchangeRate) : "",
  vendor: e?.vendor || "",
  category: e?.category || "plataformas",
  description: e?.description || "",
  justification: e?.justification || "",
  paymentMethod: e?.paymentMethod || "tarjeta_credito",
  cardLast4: e?.cardLast4 || "",
  platformId: e?.platformId || "",
  clientId: e?.clientId || "",
  attribution: attributionOf(e),
  costType: e?.costType || "",
  loss: e?.loss === true,
  lossReason: e?.lossReason || "otro",
  lossNote: e?.lossNote || "",
  accountId: e?.accountId || "",
  invoiceStatus: e?.invoiceStatus || "pendiente",
  status: e?.status || "confirmado",
  notes: e?.notes || "",
});

export default function ExpenseDialog({
  open,
  expense,
  platforms,
  accounts = [],
  onClose,
  onSaved,
}: {
  open: boolean;
  expense: Expense | null;
  platforms: Platform[];
  /** Cuentas bancarias y tarjetas (#952). */
  accounts?: ExpenseAccount[];
  onClose: () => void;
  onSaved: (e: Expense) => void;
}) {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [form, setForm] = useState<FormState>(toForm(expense));
  const [current, setCurrent] = useState<Expense | null>(expense);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Clientes de Horas Trabajadas (#980). Quien no los puede listar no ve el campo.
  const [clients, setClients] = useState<WorkHoursClientRow[]>([]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetchClients()
      .then((rows) => alive && setClients(rows))
      .catch(() => alive && setClients([]));
    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    setForm(toForm(expense));
    setCurrent(expense);
    setError(null);
    setRateSource(null);
  }, [expense, open]);

  // Tipo de cambio propuesto (#1255): el de Configuración para la fecha del gasto
  // (el fijado o, sin él, el oficial de ese día). Solo llena el campo si está
  // vacío; un tipo de cambio ya capturado o guardado nunca se pisa.
  const [rateSource, setRateSource] = useState<string | null>(null);
  useEffect(() => {
    const currency = form.currency.toUpperCase();
    if (!open || currency === "MXN" || form.exchangeRate !== "" || !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) return;
    let alive = true;
    fetchFxRate(currency, form.date)
      .then((r) => {
        if (!alive || !r || !(Number(r.rate) > 0)) return;
        setForm((f) => (f.exchangeRate === "" && f.currency.toUpperCase() === currency && f.date === r.date ? { ...f, exchangeRate: String(r.rate) } : f));
        setRateSource(r.source === "manual" ? "manual" : "oficial");
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, form.currency, form.date, form.exchangeRate]);

  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const payload = (): ExpenseInput => ({
    date: form.date,
    amount: Number(form.amount),
    currency: form.currency.toUpperCase(),
    exchangeRate: form.currency.toUpperCase() === "MXN" ? 1 : Number(form.exchangeRate) > 0 ? Number(form.exchangeRate) : null,
    vendor: form.vendor,
    category: form.category as Expense["category"],
    description: form.description,
    justification: form.justification || null,
    paymentMethod: form.paymentMethod as Expense["paymentMethod"],
    cardLast4: form.cardLast4 || null,
    platformId: form.platformId || null,
    // Solo se manda si hay clientes que elegir: quien no los ve no lo cambia.
    ...(clients.length ? { clientId: form.clientId || null } : {}),
    accountId: form.accountId || null,
    invoiceStatus: form.invoiceStatus as Expense["invoiceStatus"],
    status: form.status as Expense["status"],
    notes: form.notes || null,
    costType: (form.costType || null) as Expense["costType"],
    loss: form.loss,
    lossReason: form.loss ? form.lossReason : null,
    lossNote: form.loss ? form.lossNote || null : null,
    ...(form.attribution === "company"
      ? { personal: false, reimbursable: null }
      : form.attribution === "undecided"
        ? {}
        : { reimbursable: form.attribution === "reimbursable" }),
  });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = current ? await updateExpense(current.id, payload()) : await createExpense(payload());
      setCurrent(saved);
      onSaved(saved);
      if (current) onClose();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (files: File[], kind: "comprobante" | "factura") => {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      let latest = current;
      for (const f of files) latest = (await uploadAttachment(current.id, f, kind)).expense;
      setCurrent(latest);
      setForm((prev) => ({ ...prev, invoiceStatus: latest.invoiceStatus }));
      onSaved(latest);
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.upload"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a: ExpenseAttachment) => {
    if (!current) return;
    setBusy(true);
    try {
      const latest = await removeAttachment(current.id, a.path);
      setCurrent(latest);
      setForm((prev) => ({ ...prev, invoiceStatus: latest.invoiceStatus }));
      onSaved(latest);
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.upload"));
    } finally {
      setBusy(false);
    }
  };

  // Estado de cuenta cifrado (#1029): no hay URL firmada; se abre con el código del correo.
  const [statement, setStatement] = useState<{ id: string; label: string } | null>(null);

  const openFile = async (a: ExpenseAttachment) => {
    if (a.encrypted) {
      if (a.statementId) setStatement({ id: a.statementId, label: a.name });
      else setError(t("expenses.statements.errors.open"));
      return;
    }
    try {
      const { url } = await attachmentUrl(a.path);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.open"));
    }
  };

  const needsRate = form.currency !== "MXN" && !(Number(form.exchangeRate) > 0);
  const canSave = form.vendor.trim() && form.amount !== "" && Number(form.amount) >= 0 && /^\d{4}-\d{2}-\d{2}$/.test(form.date) && !needsRate;
  const inv = current?.invoice;

  return (
    <>
    <Dialog
      open={open}
      wide
      title={current ? t("expenses.form.editTitle") : t("expenses.form.newTitle")}
      onClose={onClose}
      footer={
        <>
          {busy && <Spinner label={t("expenses.saving")} />}
          <button type="button" className={c.secondary} onClick={onClose}>
            {current ? t("expenses.close") : t("expenses.cancel")}
          </button>
          <button type="button" className={c.primary} disabled={!canSave || busy} onClick={save}>
            {current ? t("expenses.save") : t("expenses.form.createAndAttach")}
          </button>
        </>
      }
    >
      <ErrorBox message={error} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Field label={t("expenses.fields.date")}>
          <input type="date" className={c.input} value={form.date} onChange={set("date")} />
        </Field>
        <Field label={t("expenses.fields.amount")}>
          <input type="number" min="0" step="0.01" className={c.input} value={form.amount} onChange={set("amount")} placeholder="0.00" />
        </Field>
        <Field label={t("expenses.fields.currency")}>
          <select className={c.input} value={form.currency} onChange={set("currency")}>
            {["MXN", "USD", "EUR"].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </Field>
        <Field label={t("expenses.fields.exchangeRate")}>
          <input type="number" min="0" step="0.0001" className={`${c.input} ${needsRate ? "border-amber-500" : ""}`} value={form.currency === "MXN" ? "1" : form.exchangeRate} disabled={form.currency === "MXN"} onChange={set("exchangeRate")} placeholder={t("expenses.form.ratePlaceholder")} />
        </Field>
        <Field label={t("expenses.fields.vendor")} className="sm:col-span-2">
          <input className={c.input} value={form.vendor} onChange={set("vendor")} placeholder="Google Cloud, Amazon, Telmex…" />
        </Field>
        <Field label={t("expenses.fields.category")}>
          <select className={c.input} value={form.category} onChange={set("category")}>
            {categoryOptions(i18n.language, { include: form.category }).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("expenses.fields.platform")}>
          <select className={c.input} value={form.platformId} onChange={set("platformId")}>
            <option value="">{t("expenses.fields.noPlatform")}</option>
            {platforms.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        {clients.length > 0 && (
          <Field label={t("expenses.client.label")}>
            <select aria-label={t("expenses.client.label")} className={c.input} value={form.clientId} onChange={set("clientId")}>
              <option value="">{t("expenses.client.none")}</option>
              {clients
                .filter((cl) => cl.active !== false || cl.id === form.clientId)
                .map((cl) => (
                  <option key={cl.id} value={cl.id}>
                    {cl.name}
                  </option>
                ))}
            </select>
          </Field>
        )}
        <Field label={t("expenses.fields.attribution")}>
          <select aria-label={t("expenses.fields.attribution")} className={c.input} value={form.attribution} onChange={set("attribution")}>
            {/* Un cargo de estado de cuenta lo pagó una persona: solo es reembolsable o no. */}
            {expense?.source === "estado_cuenta" ? (
              form.attribution === "undecided" && (
                <option value="undecided" disabled>
                  {t("expenses.attribution.pendingShort")}
                </option>
              )
            ) : (
              <option value="company">{t("expenses.attribution.company")}</option>
            )}
            <option value="reimbursable">{t("expenses.attribution.reimbursable")}</option>
            <option value="not_reimbursable">{t("expenses.attribution.notReimbursable")}</option>
          </select>
        </Field>
        <Field label={t("expenses.fields.costType")}>
          <select aria-label={t("expenses.fields.costType")} className={c.input} value={form.costType} onChange={set("costType")}>
            <option value="">
              {t("expenses.costType.byCategory", { type: t(`expenses.costType.${expenseCostType({ category: form.category as Expense["category"] })}Short`) })}
            </option>
            <option value="fijo">{t("expenses.costType.fijo")}</option>
            <option value="variable">{t("expenses.costType.variable")}</option>
          </select>
        </Field>
        <div className="sm:col-span-2">
          <label className={`flex items-center gap-2 text-sm ${c.text}`}>
            <input
              type="checkbox"
              className="h-4 w-4 accent-red-600"
              data-testid="expense-loss"
              checked={form.loss}
              onChange={(e) => setForm((f) => ({ ...f, loss: e.target.checked }))}
            />
            {t("expenses.losses.markAsLoss")}
          </label>
          {form.loss && (
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t("expenses.losses.reason")}>
                <select aria-label={t("expenses.losses.reason")} className={c.input} value={form.lossReason} onChange={set("lossReason")}>
                  {LOSS_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {LOSS_REASON_LABELS[r][i18n.language?.startsWith("en") ? "en" : "es"]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("expenses.losses.note")}>
                <input className={c.input} value={form.lossNote} onChange={set("lossNote")} placeholder={t("expenses.losses.noteHint")} />
              </Field>
            </div>
          )}
        </div>
        <Field label={t("expenses.fields.description")} className="sm:col-span-2">
          <textarea rows={2} className={c.input} value={form.description} onChange={set("description")} placeholder={t("expenses.fields.descriptionHint")} />
        </Field>
        <Field label={t("expenses.fields.justification")} className="sm:col-span-2">
          <textarea rows={2} className={c.input} value={form.justification} onChange={set("justification")} placeholder={t("expenses.fields.justificationHint")} />
        </Field>
        <Field label={t("expenses.fields.paymentMethod")}>
          <select className={c.input} value={form.paymentMethod} onChange={set("paymentMethod")}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`expenses.payment.${m}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("expenses.fields.card")}>
          <input className={c.input} maxLength={4} inputMode="numeric" value={form.cardLast4} onChange={set("cardLast4")} placeholder="1234" />
        </Field>
        {accounts.length > 0 && (
          <Field label={t("expensesStatements:common.account")}>
            <select aria-label="expense-account" className={c.input} value={form.accountId} onChange={set("accountId")}>
              <option value="">{t("expensesStatements:common.noAccount")}</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {accountLabel(a)}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label={t("expenses.fields.invoice")}>
          <select className={c.input} value={form.invoiceStatus} onChange={set("invoiceStatus")}>
            {INVOICE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`expenses.invoice.${s}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("expenses.fields.status")}>
          <select className={c.input} value={form.status} onChange={set("status")}>
            <option value="confirmado">{t("expenses.status.confirmado")}</option>
            <option value="por_revisar">{t("expenses.status.por_revisar")}</option>
          </select>
        </Field>
        <Field label={t("expenses.fields.notes")} className="sm:col-span-4">
          <input className={c.input} value={form.notes} onChange={set("notes")} />
        </Field>
      </div>
      {needsRate && <p className="mt-2 text-xs text-amber-600">{t("expenses.form.rateRequired")}</p>}
      {!needsRate && form.currency !== "MXN" && rateSource && (
        <p className={`mt-2 text-xs ${c.muted}`} data-testid="rate-suggested">
          {t(rateSource === "manual" ? "expenses.form.rateSuggestedPinned" : "expenses.form.rateSuggestedOfficial")}
        </p>
      )}
      {form.currency !== "MXN" && !needsRate && Number(form.amount) > 0 && (
        <p className={`mt-2 text-xs ${c.muted}`}>
          {t("expenses.form.baseAmount", { amount: money(Number(form.amount) * (Number(form.exchangeRate) || 1), "MXN", i18n.language) })}
        </p>
      )}

      <div className={`mt-5 border-t pt-4 ${c.border}`}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className={`text-sm font-semibold ${c.text}`}>{t("expenses.files.title")}</h3>
          {current && <Chip className={INVOICE_TONES[current.invoiceStatus]}>{t(`expenses.invoice.${current.invoiceStatus}`)}</Chip>}
        </div>
        {!current ? (
          <p className={`text-xs ${c.muted}`}>{t("expenses.files.saveFirst")}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FileDrop accept="image/*,application/pdf" multiple disabled={busy} label={t("expenses.files.receipt")} hint={t("expenses.files.receiptHint")} onFiles={(f) => upload(f, "comprobante")} />
              <FileDrop accept="application/pdf,.xml,text/xml,application/xml" multiple disabled={busy} label={t("expenses.files.invoice")} hint={t("expenses.files.invoiceHint")} onFiles={(f) => upload(f, "factura")} />
            </div>
            {inv && (
              <div className={`mt-3 rounded-lg p-3 text-xs ${c.surface} ${c.text}`} data-testid="invoice-data">
                <p className="font-semibold">{inv.emisorNombre || inv.emisorRfc || t("expenses.files.invoice")}</p>
                <p className={c.muted}>
                  {[inv.emisorRfc, inv.uuid && `${t("expenses.files.uuid")}: ${inv.uuid}`, inv.fecha?.slice(0, 10)].filter(Boolean).join(" · ")}
                </p>
                <p>
                  {t("expenses.files.invoiceTotal")}: <strong>{money(inv.total, inv.moneda || "MXN", i18n.language)}</strong>
                  {current.amount != null && inv.total != null && Math.abs(inv.total - current.amount) > 0.01 && (
                    <span className="ml-2 text-amber-600">{t("expenses.files.totalMismatch")}</span>
                  )}
                </p>
              </div>
            )}
            <ul className="mt-3 space-y-1">
              {(current.attachments || []).map((a) => (
                <li key={a.path} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${c.border}`}>
                  <button type="button" onClick={() => openFile(a)} className={`flex min-w-0 items-center gap-2 text-left ${c.text}`}>
                    <i className={a.contentType === "application/pdf" ? "ri-file-pdf-2-line text-red-500" : a.contentType.includes("xml") ? "ri-file-code-line text-sky-500" : "ri-image-line text-teal-500"} />
                    <span className="truncate">{a.name}</span>
                    {a.encrypted && <i className="ri-lock-2-line text-teal-600" title={t("expenses.statements.encrypted")} aria-label={t("expenses.statements.encrypted")} />}
                    <Chip className="bg-gray-400/15 text-gray-500">{t(`expenses.files.kind.${a.kind}`)}</Chip>
                  </button>
                  <button type="button" aria-label={`remove-${a.name}`} onClick={() => remove(a)} className="text-red-500" disabled={busy}>
                    <i className="ri-delete-bin-line" />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Dialog>
    <StatementViewer statementId={statement?.id || null} label={statement?.label} onClose={() => setStatement(null)} />
    </>
  );
}
